<template>
  <NavBar pageName="Settings"></NavBar>
  <v-container style="margin-top: 185px;">
    <div class="d-flex align-center mb-4">
      <v-btn icon variant="text" size="small" @click="$router.push('/settings')" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h2>
        <router-link to="/settings" class="text-decoration-none" style="color: inherit; opacity: 0.7;">Settings</router-link>
        <span class="mx-2" style="opacity: 0.4;">></span>
        <span>{{ categoryTitle }}</span>
      </h2>
    </div>

    <v-list v-if="categoryLinks.length > 0">
      <v-list-item v-for="link in filteredLinks" :key="link.text" router :to="link.route">
        <v-list-item-action>
          <v-icon class="white--text">{{ link.icon }}</v-icon>
          <v-list-item-title>{{ link.text }}</v-list-item-title>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <v-alert v-else type="warning" variant="tonal" class="mt-4">
      Category not found.
    </v-alert>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import NavBar from "@/components/NavBar.vue";
import store from "@/store";

const categoryMap = {
  imsi: {
    title: 'IMSI',
    links: [
      { text: 'IMSI Types', icon: 'mdi-shape', route: '/imsiTypesSettings', roles: ['root', 'admin', 'general', 'manager_sectiune', 'super_admin'] },
      { text: 'IMSI Prices', icon: 'mdi-sim', route: '/imsiPricesSettings', roles: ['root', 'admin', 'general', 'manager_sectiune', 'super_admin'] },
      { text: 'IMSI Inventory', icon: 'mdi-database-plus', route: '/imsiInventory', roles: ['root', 'admin'] },
    ]
  },
  sim: {
    title: 'SIM',
    links: [
      // Settings for SIM will be added here as new features are implemented
    ]
  },
  msisdn: {
    title: 'MSISDN',
    links: [
      { text: 'MSISDN Classes', icon: 'mdi-phone-classic', route: '/msisdnClassSettings', roles: ['root', 'admin', 'general', 'manager_sectiune', 'super_admin'] },
      { text: 'MSISDN Prices', icon: 'mdi-currency-eur', route: '/msisdnPricesSettings', roles: ['root', 'admin', 'general', 'manager_sectiune', 'super_admin'] },
      { text: 'MSISDN Inventory', icon: 'mdi-database-plus', route: '/msisdnInventory', roles: ['root', 'admin'] },
    ]
  },
  services: {
    title: 'Services',
    links: [
      { text: 'Service Types', icon: 'mdi-tag-multiple', route: '/serviceTypesSettings', roles: ['root', 'admin', 'general', 'manager_sectiune', 'super_admin'] },
      { text: 'Service Limitations', icon: 'mdi-speedometer', route: '/serviceLimitationsSettings', roles: ['root', 'admin', 'super_admin', 'distributor', 'default_admin_l0', 'standard_admin_l0'] },
      { text: 'Service MVNO Operations', icon: 'mdi-api', route: '/serviceMvnoOperations', roles: ['root', 'admin'] },
      { text: 'Distributor Service Products', icon: 'mdi-link-variant', route: '/distributorServiceProducts', roles: ['root'] },
      { text: 'Service Quarantine Durations', icon: 'mdi-shield-lock-outline', route: '/serviceQuarantineDurations', roles: ['root', 'admin'] },
    ]
  },
  'client-services': {
    title: 'Client Services',
    links: [
      { text: 'Validity Options', icon: 'mdi-calendar-clock', route: '/validityPurchaseOptions', roles: ['root', 'admin', 'super_admin', 'distributor', 'default_admin_l0', 'standard_admin_l0'] },
      { text: 'Validity Limits', icon: 'mdi-shield-check', route: '/validityOptionLimits', roles: ['root', 'admin', 'super_admin'] },
    ]
  },
  goods: {
    title: 'Goods',
    links: [
      { text: 'Goods Inventory', icon: 'mdi-package-variant', route: '/goodsInventory', roles: ['root', 'admin', 'general', 'manager_sectiune', 'super_admin', 'distributor', 'default_admin_l0', 'standard_admin_l0'] },
      { text: 'Good Categories', icon: 'mdi-tag-multiple', route: '/goodCategories', roles: ['root', 'admin', 'general', 'manager_sectiune', 'super_admin', 'distributor', 'default_admin_l0', 'standard_admin_l0'] },
    ]
  },
  inventory: {
    title: 'Inventory',
    links: [
      { text: 'Inventory Statuses', icon: 'mdi-list-status', route: '/inventoryStatuses', roles: ['root', 'admin', 'general', 'manager_sectiune', 'super_admin', 'distributor', 'default_admin_l0', 'standard_admin_l0'] },
    ]
  },
  templates: {
    title: 'Templates',
    links: [
      { text: 'Dashboard Templates', icon: 'mdi-view-dashboard-outline', route: '/dashboardTemplatesSettings', roles: ['root', 'admin', 'super_admin'] },
    ]
  },
  notifications: {
    title: 'Notifications',
    links: [
      { text: 'Notification Templates', icon: 'mdi-email-outline', route: '/notificationTemplatesSettings', roles: ['root', 'admin'] },
    ]
  },
  admin: {
    title: 'Administrator',
    links: [
      { text: 'Import Veridian', icon: 'mdi-database-import', route: '/settings/admin-veridian-import', roles: ['root', 'default_admin_l0'] },
    ]
  }
};

export default defineComponent({
  name: 'SettingsCategoryView',
  components: { NavBar },
  data() {
    return {
      userRoles: [],
      token: '',
    };
  },
  computed: {
    category() {
      return this.$route.params.category;
    },
    categoryTitle() {
      return categoryMap[this.category]?.title || 'Unknown';
    },
    categoryLinks() {
      return categoryMap[this.category]?.links || [];
    },
    filteredLinks() {
      if (this.userRoles.length === 0 && this.token) {
        return this.categoryLinks;
      }
      return this.categoryLinks.filter(link => {
        if (link.roles && link.roles.length > 0) {
          return link.roles.some(role => this.userRoles.includes(role));
        }
        return true;
      });
    }
  },
  created() {
    const { getLoggedIn } = useGetLoggedIn();
    getLoggedIn().then(() => {
      this.token = store.getters.getToken;
      const currentUser = store.getters.getUser;

      if (currentUser && currentUser.roles && Array.isArray(currentUser.roles) && currentUser.roles.length > 0) {
        this.userRoles = currentUser.roles.map(r => {
          if (typeof r === 'string') return r;
          if (r && r.name) return r.name;
          if (r && r.role_name) return r.role_name;
          return String(r);
        });
      } else if (currentUser && currentUser.user_roles && Array.isArray(currentUser.user_roles)) {
        this.userRoles = currentUser.user_roles.map(r => {
          if (typeof r === 'string') return r;
          if (r && r.name) return r.name;
          if (r && r.role_name) return r.role_name;
          if (r && r.role && r.role.name) return r.role.name;
          return String(r);
        });
      } else if (currentUser && currentUser.role) {
        this.userRoles = [currentUser.role];
      } else if (currentUser && currentUser.role_name) {
        this.userRoles = [currentUser.role_name];
      } else {
        this.userRoles = ['general'];
      }
    });
  }
});
</script>
